import { Grid, IconButton, Tab, Tabs } from "@mui/material";
import {
  getCountyOptionsByCountry,
  leadDeliveryAllFields,
} from "./leadAllFields";
import FormField from "../../../components/common/FormField";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  addDeliveryAddress,
  deleteLeadAddressAction,
  getCalculateDistanceAction,
  removeDeliveryAddress,
  setCalculateDeliveryDistanceAction,
  setNewLeadLocal,
} from "../../../store/reducers/leadSlice";
import CustomButton from "../../../components/common/button/CustomButton";
import { RemoveCircleRounded } from "@mui/icons-material";

const DEFAULT_PROPERTY_TYPES = [
  "Unknown",
  "House",
  "Town House",
  "Mainsonette",
  "Flat",
  "Bungalow",
  "Cottage",
  "Self Store",
  "Commercial",
];

const DEFAULT_POSTCODE_TRIGGER_LENGTH = 6;

const POSTCODE_TRIGGER_LENGTH_BY_COUNTRY: Record<string, number> = {
  England: 5,
  Wales: 5,
  Scotland: 5,
  "Northern Ireland": 5,
  India: 6,
  Ireland: 7,
  Spain: 5,
  Denmark: 4,
  France: 5,
  Netherlands: 6,
  Portugal: 7,
  USA: 5,
  "New Zealand": 4,
  Australia: 4,
  Australiya: 4,
  Canada: 6,
  Dubai: 3,
};

const getPostcodeTriggerLength = (country?: string) => {
  if (!country) return DEFAULT_POSTCODE_TRIGGER_LENGTH;
  return (
    POSTCODE_TRIGGER_LENGTH_BY_COUNTRY[country] ||
    DEFAULT_POSTCODE_TRIGGER_LENGTH
  );
};

const normalizePostcode = (postcode?: string) =>
  (postcode || "").toString().replace(/\s|-/g, "");

interface ManageDeliveryFormProps {
  checkValidator?: any;
  resetValidator?: any;
  error?: any;
  formData?: any;
  setFormData?: any;
}

const DeliveryForm: FC<ManageDeliveryFormProps> = ({
  checkValidator,
  resetValidator,
  error,
  formData,
  setFormData,
}) => {
  const dispatch = useDispatch();
  const { propertyTypeList } = useSelector((state: any) => state.leadSurvey);

  const { userProfile } = useSelector((state: any) => state.user);
  const { newLeadLocal, distanceDeliveryValue, distanceReturnValue } =
    useSelector((state: any) => state.lead);
  const lastDeliveryDistanceRequestRef = useRef("");
  const lastReturnDistanceRequestRef = useRef("");
  // Helper: Check if all delivery addresses are filled (postcode & country)
  const isValidDistance = (d: any) => {
    const num = typeof d === "string" ? parseFloat(d) : d;
    return (
      num !== undefined && num !== null && !isNaN(num) && num !== "" && num > 0
    );
  };
  const areAllDeliveriesCompleted = () => {
    if (
      !Array.isArray(newLeadLocal?.deliveryAddress) ||
      newLeadLocal.deliveryAddress.length === 0
    )
      return false;
    return newLeadLocal.deliveryAddress.every((addr: any) => {
  const postcode = addr?.postcode?.toString().replace(/\s|-/g, "") || "";

  return (
    postcode.length >=
      getPostcodeTriggerLength(addr?.country) &&
    isValidDistance(addr.distance)
  );
});
  };
  const deliveryAddresses = Array.isArray(newLeadLocal?.deliveryAddress)
    ? newLeadLocal.deliveryAddress
    : [];
  const collectionAddresses = Array.isArray(newLeadLocal?.collectionAddress)
    ? newLeadLocal.collectionAddress
    : [];
  const lastDeliveryAddress =
    deliveryAddresses[deliveryAddresses.length - 1] || null;
  const lastDeliveryPostcode = lastDeliveryAddress?.postcode || "";
  const lastDeliveryCountry = lastDeliveryAddress?.country || "";
  const allDeliveriesCompleted = areAllDeliveriesCompleted();
  const deliveryDistanceKey = deliveryAddresses
    .map(
      (addr: any) =>
        `${normalizePostcode(addr?.postcode)}|${addr?.country || ""}|${addr?.distance || ""}`,
    )
    .join("::");
  // Effect: Clear return distance if a new delivery is added (so it recalculates for the new last delivery)
  useEffect(() => {
    if (
      newLeadLocal.deliveryAddress.length > 1 &&
      !areAllDeliveriesCompleted() &&
      newLeadLocal.returningDistance
    ) {
      dispatch(
        setNewLeadLocal({
          ...newLeadLocal,
          returningDistance: 0,
        }),
      );
    }
  }, [newLeadLocal.deliveryAddress.length]);

  // Effect: Calculate return distance only when all deliveries are valid, always from the last delivery
  useEffect(() => {
    if (!allDeliveriesCompleted) return;
    if (!lastDeliveryPostcode) return;
    const normalizedLastDeliveryPostcode = normalizePostcode(lastDeliveryPostcode);
    const lastDeliveryRequiredLength = getPostcodeTriggerLength(
      lastDeliveryCountry,
    );
    if (normalizedLastDeliveryPostcode.length < lastDeliveryRequiredLength)
      return;

    const returnRequestKey = `${normalizedLastDeliveryPostcode}|${lastDeliveryCountry}|${normalizePostcode(userProfile?.transportCode)}|${userProfile?.country || ""}`;
    if (lastReturnDistanceRequestRef.current === returnRequestKey) return;

    if (
      newLeadLocal.returningDistance &&
      parseFloat(newLeadLocal.returningDistance) > 0 &&
      distanceReturnValue &&
      parseFloat(distanceReturnValue) ===
        parseFloat(newLeadLocal.returningDistance)
    ) {
      return;
    }
    lastReturnDistanceRequestRef.current = returnRequestKey;
    dispatch(
      getCalculateDistanceAction({
        postcode1: lastDeliveryPostcode,
        country1: lastDeliveryCountry ?? "",
        postcode2: userProfile?.transportCode,
        country2: userProfile?.country ?? "",
        type: "return",
      }),
    );
  }, [
    allDeliveriesCompleted,
    deliveryDistanceKey,
    lastDeliveryPostcode,
    lastDeliveryCountry,
    userProfile?.transportCode,
    userProfile?.country,
    dispatch,
    distanceReturnValue,
    newLeadLocal.returningDistance,
  ]);

  // Effect: Sync distanceReturnValue to newLeadLocal.returningDistance
  useEffect(() => {
    if (
      areAllDeliveriesCompleted() &&
      distanceReturnValue &&
      parseFloat(distanceReturnValue) > 0 &&
      parseFloat(distanceReturnValue) !==
        parseFloat(newLeadLocal.returningDistance)
    ) {
      dispatch(
        setNewLeadLocal({
          ...newLeadLocal,
          returningDistance: distanceReturnValue,
        }),
      );
    }
  }, [distanceReturnValue, newLeadLocal, dispatch]);

  const [isBasicDiarySection, setIsBasicDiarySection] = useState(
    userProfile?.features?.includes("Basic Work Diary") || false, // Safe fallback
  );

  useEffect(() => {
    setIsBasicDiarySection(
      userProfile?.features?.includes("Basic Work Diary") || false,
    ); // Sync profile changes
  }, [userProfile]);

  // State to track manual changes to the postcode field
  const [isPostcodeManuallyChanged, setIsPostcodeManuallyChanged] =
    useState(false);

  const [totalDistanceValue, setTotalDistanceValue] = useState(0);
  // Conversion functions
  const convertToFeetCubed = (volumeInMetersCubed: number) =>
    (volumeInMetersCubed * 35.3147).toFixed(2);

  const convertToMetersCubed = (volumeInFeetCubed: number) =>
    (volumeInFeetCubed / 35.3147).toFixed(2);

  // Handle unit change and convert volume if necessary
  const handleUnitChange = (unit: string, deliveryVolume?: number) => {
    const currentAddress = newLeadLocal?.deliveryAddress?.[activeTab] || {};
    const currentVolume = deliveryVolume ?? newLeadLocal?.deliveryVolume;
    const currentUnit = newLeadLocal?.deliveryVolumeUnit;

    let newDeliveryVolume = currentVolume;

    if (unit === "ft3" && currentUnit === "m3") {
      newDeliveryVolume = convertToFeetCubed(currentVolume);
    } else if (unit === "m3" && currentUnit === "ft3") {
      newDeliveryVolume = convertToMetersCubed(currentVolume);
    }

    // ✅ Update Redux state
    const updatedDeliveryAddress = [...(newLeadLocal.deliveryAddress || [])];
    updatedDeliveryAddress[activeTab] = {
      ...currentAddress,
      deliveryVolume: Number(newDeliveryVolume) || 0,
      deliveryVolumeUnit: unit,
    };

    dispatch(
      setNewLeadLocal({
        ...newLeadLocal,
        deliveryAddress: updatedDeliveryAddress,
      }),
    );

    setFormData((prevState: any) => {
      const updatedAddresses = [...(prevState.deliveryAddress || [])];
      updatedAddresses[activeTab] = {
        ...updatedAddresses[activeTab],
        deliveryVolume: Number(newDeliveryVolume) || 0,
        deliveryVolumeUnit: unit,
      };

      return {
        ...prevState,
        deliveryAddress: updatedAddresses,
      };
    });
  };

  // State to manage the active tab
  const [activeTab, setActiveTab] = useState(0);

  const isDeliveryField = (fieldId: string) =>
    [
      "building_number",
      "street",
      "town",
      "county",
      "postcode",
      "country",
      "purchaseStatus",
      "houseSize",
      "distance",
      "volume",
      "volumeUnit",
      "type",
    ].includes(fieldId);
  // Handle input changes
  const onChangeAction = (event: any) => {
    const { id, value } = event.target;

    const isDeliveryField = [
      "building_number",
      "street",
      "town",
      "county",
      "postcode",
      "country",
      "purchaseStatus",
      "houseSize",
      "distance",
      "volume",
      "volumeUnit",
      "type",
    ].includes(id);

    let updatedDeliveryAddress = [...(newLeadLocal.deliveryAddress || [])];

    if (isDeliveryField && updatedDeliveryAddress[activeTab]) {
      updatedDeliveryAddress[activeTab] = {
        ...updatedDeliveryAddress[activeTab],
        [id]: id === "postcode" ? value?.toUpperCase() || value : value,
      };
    } else if (id === "moveType") {
      dispatch(
        setNewLeadLocal({
          ...newLeadLocal,
          [id]: value,
        }),
      );
      setFormData((prevState: any) => ({
        ...prevState,
        [id]: value,
      }));
    }

    const updatedNewLeadLocal = {
      ...newLeadLocal,
      ...(isDeliveryField
        ? {
            deliveryAddress: updatedDeliveryAddress,
          }
        : {
            [id]:
              id === "postcode" ? (value ? value.toUpperCase() : value) : value,
          }),
    };

    dispatch(setNewLeadLocal(updatedNewLeadLocal));
    // ✅ Update formData for the specific active delivery address
    setFormData((prevState: any) => {
      if (isDeliveryField) {
        const updatedAddresses = [...(prevState.deliveryAddress || [])];
        updatedAddresses[activeTab] = {
          ...updatedAddresses[activeTab],
          [id]: value,
        };
        return {
          ...prevState,
          deliveryAddress: updatedAddresses,
        };
      } else {
        return {
          ...prevState,
          [id]: value,
        };
      }
    });

    // Track manual changes to the postcode field
    if (id === "postcode") {
      setIsPostcodeManuallyChanged(true);
    } else if (id === "country") {
      const postcodeForSelectedCountry =
        updatedDeliveryAddress?.[activeTab]?.postcode || "";
      const normalizedPostcode = postcodeForSelectedCountry
        .toString()
        .replace(/\s|-/g, "");
      const requiredLength = getPostcodeTriggerLength(value);

      setIsPostcodeManuallyChanged(
        normalizedPostcode.length === requiredLength,
      );
    } else {
      setIsPostcodeManuallyChanged(false);
    }

    if (id === "deliveryVolumeUnit") {
      // Handle unit change
      handleUnitChange(value);
    } else {
      // Handle value change for other fields
      const updatedLead = {
        ...formData,
        [id]: value,
      };

      if (id === "deliveryVolume") {
        // If the collectionVolume changes, convert the value according to the current unit
        handleUnitChange(formData?.deliveryVolumeUnit, Number(value));
      } else {
        setFormData(updatedLead);
      }
    }

    resetValidator(id);
  };

  // Set county dropdown options based on country
  const getCountyOptions = (country: string) => {
    return getCountyOptionsByCountry(country);
  };

  const getPropertyTypeOptions = (index: number) => {
    const configuredPropertyTypes = Array.isArray(propertyTypeList)
      ? propertyTypeList
          .map((item: any) => item?.name)
          .filter((name: any) => typeof name === "string" && name.trim() !== "")
      : [];

    const baseOptions =
      configuredPropertyTypes.length > 0
        ? configuredPropertyTypes
        : DEFAULT_PROPERTY_TYPES;

    const savedTypeName = newLeadLocal?.deliveryAddress?.[index]?.type;
    const hasSavedTypeInOptions =
      typeof savedTypeName === "string" &&
      baseOptions.some(
        (option: string) =>
          option.toLowerCase() === savedTypeName.toLowerCase(),
      );

    if (
      typeof savedTypeName === "string" &&
      savedTypeName.trim() !== "" &&
      !hasSavedTypeInOptions
    ) {
      return [...baseOptions, savedTypeName];
    }

    return baseOptions;
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  // Effect to check default unit value on mount
  useEffect(() => {
    handleUnitChange(formData?.deliveryVolumeUnit || "m3");
  }, []);

  const activeDeliveryAddress = deliveryAddresses[activeTab] || {};
  const activeDeliveryPostcode = activeDeliveryAddress?.postcode || "";
  const activeDeliveryCountry = activeDeliveryAddress?.country || "";
  const previousDeliveryAddress = activeTab > 0 ? deliveryAddresses[activeTab - 1] : null;
  const previousCollectionAddress =
    activeTab === 0 ? collectionAddresses[collectionAddresses.length - 1] : null;
  const previousDeliveryPostcode = previousDeliveryAddress?.postcode || "";
  const previousDeliveryCountry = previousDeliveryAddress?.country || "";
  const previousCollectionPostcode = previousCollectionAddress?.postcode || "";
  const previousCollectionCountry = previousCollectionAddress?.country || "";

  
  // Effect to call distance API
  useEffect(() => {
    const currentPostcodeRaw = activeDeliveryPostcode;
    const currentCountry = activeDeliveryCountry;
    const normalizedCurrentPostcode = normalizePostcode(currentPostcodeRaw);
    const currentRequiredLength = getPostcodeTriggerLength(currentCountry);

    if (
      isPostcodeManuallyChanged &&
      normalizedCurrentPostcode.length >= currentRequiredLength
    ) {
      let previousPostcode = userProfile?.transportCode || "";
      let previousCountry = userProfile?.country ?? "";
      if (activeTab === 0) {
        const previousPostcodeRaw = previousCollectionPostcode;
        previousCountry = previousCollectionCountry;
        const normalizedPreviousPostcode = normalizePostcode(previousPostcodeRaw);
        const previousRequiredLength =
          getPostcodeTriggerLength(previousCountry);

        if (normalizedPreviousPostcode.length >= previousRequiredLength) {
          previousPostcode = previousCollectionPostcode;
        }
      } else {
        const previousPostcodeRaw = previousDeliveryPostcode;
        previousCountry = previousDeliveryCountry;
        const normalizedPreviousPostcode = normalizePostcode(previousPostcodeRaw);
        const previousRequiredLength =
          getPostcodeTriggerLength(previousCountry);

        if (normalizedPreviousPostcode.length >= previousRequiredLength) {
          previousPostcode = previousDeliveryPostcode;
        }
      }

      const deliveryRequestKey = `${activeTab}|${normalizePostcode(previousPostcode)}|${previousCountry || ""}|${normalizedCurrentPostcode}|${currentCountry || ""}`;
      if (lastDeliveryDistanceRequestRef.current === deliveryRequestKey) {
        setIsPostcodeManuallyChanged(false);
        return;
      }
      lastDeliveryDistanceRequestRef.current = deliveryRequestKey;

      const postcodeData = {
        postcode1: previousPostcode,
        country1: previousCountry,
        postcode2: currentPostcodeRaw,
        country2: currentCountry,
        type: "delivery",
      };

      dispatch(getCalculateDistanceAction(postcodeData));
      setIsPostcodeManuallyChanged(false);
    }
  }, [
    activeDeliveryPostcode,
    activeDeliveryCountry,
    previousDeliveryPostcode,
    previousDeliveryCountry,
    previousCollectionPostcode,
    previousCollectionCountry,
    userProfile?.transportCode,
    userProfile?.country,
    dispatch,
    isPostcodeManuallyChanged,
    activeTab,
  ]);

  // Effect to update formData and Redux only for delivery segment distances (never for return)
  useEffect(() => {
    if (
      distanceDeliveryValue &&
      typeof activeTab === "number" &&
      newLeadLocal.deliveryAddress[activeTab] &&
      // Only update if value is not already set (prevents double update)
      newLeadLocal.deliveryAddress[activeTab].distance !== distanceDeliveryValue
    ) {
      // Defensive: Only update if not a return distance (should be handled by saga, but double check)
      setFormData((prevState: any) => ({
        ...prevState,
        deliveryAddress: prevState.deliveryAddress.map(
          (item: any, index: number) =>
            index === activeTab
              ? { ...item, distance: distanceDeliveryValue }
              : item,
        ),
      }));
      dispatch(
        setNewLeadLocal({
          ...newLeadLocal,
          deliveryAddress: newLeadLocal.deliveryAddress.map(
            (item: any, index: any) =>
              index === activeTab
                ? { ...item, distance: distanceDeliveryValue }
                : item,
          ),
        }),
      );
      dispatch(setCalculateDeliveryDistanceAction(null)); // Clear the distance value after setting it
      setIsPostcodeManuallyChanged(false);
    }
  }, [distanceDeliveryValue, activeTab, formData, dispatch]);

  // Effect: Calculate total distance, add only the latest return distance if all deliveries are completed
  useEffect(() => {
    // DEBUG: Log before calculation
    console.log(
      "TOTAL DEBUG: Collection:",
      (newLeadLocal?.collectionAddress || []).map((a: any) => a.distance),
    );
    console.log(
      "TOTAL DEBUG: Delivery:",
      (newLeadLocal?.deliveryAddress || []).map((a: any) => a.distance),
    );
    console.log("TOTAL DEBUG: Returning:", newLeadLocal?.returningDistance);
    // Only use the latest, valid return distance ONCE
    let returnDistance = 0;
    if (
      areAllDeliveriesCompleted() &&
      isValidDistance(newLeadLocal.returningDistance)
    ) {
      returnDistance = parseFloat(newLeadLocal.returningDistance);
    }

    const collectionDistancesTotal = (newLeadLocal?.collectionAddress || [])
      .map((a: any) =>
        typeof a.distance === "string" ? parseFloat(a.distance) : a.distance,
      )
      .filter(isValidDistance)
      .reduce((sum: number, d: number) => sum + d, 0);

    const deliveryDistancesTotal = (newLeadLocal?.deliveryAddress || [])
      .map((a: any) =>
        typeof a.distance === "string" ? parseFloat(a.distance) : a.distance,
      )
      .filter(isValidDistance)
      .reduce((sum: number, d: number) => sum + d, 0);

    // Final total: collection + delivery + return (only once)
    let totalVal =
      collectionDistancesTotal + deliveryDistancesTotal + returnDistance;
    totalVal = parseFloat(totalVal.toFixed(2));

    console.log("TOTAL DEBUG: Final total:", totalVal);
    setTotalDistanceValue((prevValue) =>
      prevValue !== totalVal ? totalVal : prevValue,
    );
  }, [
    newLeadLocal?.collectionAddress,
    newLeadLocal?.deliveryAddress,
    newLeadLocal?.returningDistance,
  ]);

  // DEBUG: Log when delivery distance is updated
  useEffect(() => {
    if (distanceDeliveryValue) {
      console.log(
        "DEBUG: Setting delivery distance for tab",
        activeTab,
        "value:",
        distanceDeliveryValue,
      );
    }
  }, [distanceDeliveryValue, activeTab]);

  const handleAddAddress = () => {
    dispatch(addDeliveryAddress());
  };

  const handleRemoveAddress = (index: number) => {
    dispatch(removeDeliveryAddress(index));
  };

  const handleDeleteAddress = (address: any) => {
    const currentLength = newLeadLocal.deliveryAddress.length;
    if (address?.id) {
      dispatch(
        deleteLeadAddressAction({
          leadId: address?.lead_id,
          addressId: address?.id,
        }),
      );
    } else {
      handleRemoveAddress(activeTab);
    }
    // Adjust active tab after deletion
    setTimeout(() => {
      setActiveTab((prevTab) => {
        if (currentLength <= 1) return 0;
        if (prevTab === currentLength - 1) return prevTab - 1; // if last tab deleted
        return prevTab; // stay on same index if not last
      });
    }, 0); // wait for state update
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={0.5}
        rowGap={0}
        rowSpacing={0}
        sx={{
          background: "#E5F4F1",
          padding: "10px !important",
          marginTop: "10px !important",
          borderRadius: "5px",
          border: "1px solid #CFCFCF",
          overflow: "hidden",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          style={{
            padding: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Tabs for Delivery Addresses */}
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            aria-label="Delivery Address Tabs"
            TabIndicatorProps={{ style: { display: "none" } }} // Hide the tab indicator line
            sx={{
              minHeight: "32px",
              "& .MuiTabs-flexContainer": {
                gap: 1, // adds space between tabs (theme.spacing(1))
              },
            }}
          >
            {newLeadLocal.deliveryAddress?.map(
              (address: any, index: number) => (
                <Tab
                  sx={{
                    textAlign: "left",
                    padding: "0px",
                    fontWeight: 600,
                    color: "#333333",
                    marginRight: "10px",
                    backgroundColor:
                      activeTab === index ? "#ffffff" : "transparent",
                    borderRadius: "5px",
                    minHeight: "30px !important",
                    paddingLeft: "6px !important",
                  }}
                  key={index}
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ marginRight: 4 }}>{`Delivery ${
                        newLeadLocal?.deliveryAddress?.length > 1
                          ? index + 1
                          : ""
                      }`}</span>
                      {newLeadLocal?.deliveryAddress?.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // prevent tab change
                            handleDeleteAddress(address); // your custom delete function
                          }}
                          disabled={activeTab !== index} // Disable if it's the active tab
                          sx={{
                            cursor:
                              activeTab === index ? "pointer" : "not-allowed",
                          }}
                        >
                          <RemoveCircleRounded
                            sx={{
                              color: activeTab === index ? "#dd4247" : "#ccc",
                            }}
                            fontSize="small"
                          />
                        </IconButton>
                      )}
                    </div>
                  }
                />
              ),
            )}
          </Tabs>
          {newLeadLocal.deliveryAddress?.length < 3 && !isBasicDiarySection && (
            <CustomButton
              style={{ padding: "6px 16px", height: "26px" }}
              title="Add"
              onClick={handleAddAddress}
            />
          )}
        </Grid>
        {newLeadLocal.deliveryAddress?.map((address: any, index: number) => (
          <Grid
            item
            xs={12}
            key={index}
            role="tabpanel"
            hidden={activeTab !== index} // Hide tabs based on the activeTab value
            id={`tab-panel-${index}`}
            aria-labelledby={`tab-${index}`}
            sx={{
              paddingLeft: "0px !important",
            }}
          >
            {activeTab === index && (
              <Grid container spacing={0.5}>
                {leadDeliveryAllFields
                  ?.filter((field) => {
                    if (isBasicDiarySection && field?.id === "moveType")
                      return false; // Exclude moveType field if Basic Diary Section is enabled
                    return true;
                  })
                  .map((field, i) => {
                    return (
                      <FormField
                        onPaste={(e: any) =>
                          field?.id === "street" ||
                          field?.id === "town" ||
                          field?.id === "postcode"
                            ? ""
                            : e.preventDefault()
                        }
                        onKeyDown={(event: any) => {
                          console.log(
                            "event log-",
                            event,
                            event.target.value + event.key,
                          );
                          if (
                            event.keyCode === 8 ||
                            event.keyCode === 9 ||
                            event.keyCode === 37 ||
                            event.keyCode === 38 ||
                            event.keyCode === 39 ||
                            event.keyCode === 40 ||
                            event.keyCode === 18 ||
                            event.keyCode === 17 ||
                            event.ctrlKey
                          )
                            return event;
                          if (
                            field?.patternAllowed &&
                            !field?.patternAllowed.test(event.key)
                          ) {
                            event.preventDefault();
                          } else if (
                            field?.patternAllowed &&
                            !field?.patternAllowed.test(
                              event.target.value + event.key,
                            )
                          ) {
                            event.preventDefault();
                          }
                        }}
                        {...{
                          ...field,
                          value:
                            field?.id === "totalDistance"
                              ? totalDistanceValue
                              : isDeliveryField(field?.id)
                                ? newLeadLocal.deliveryAddress?.[activeTab]?.[
                                    field.id
                                  ] || ""
                                : newLeadLocal?.[field.id] || "",
                          inSingleGridForm: true,
                          options:
                            field.id === "county"
                              ? getCountyOptions(
                                  newLeadLocal?.deliveryAddress?.[activeTab]
                                    ?.country,
                                )
                              : field.id === "type"
                                ? getPropertyTypeOptions(activeTab)
                                : field.options,
                          ...{
                            variant: "outlined", // Adding this variant prop
                            onChange: onChangeAction,
                            disabled:
                              field?.id === "totalDistance" ? true : false,
                            onBlur: onBlurAction,
                            isNonMarginTop: true,
                            InputProps: {
                              ...(field?.inputProps && field?.inputProps),
                              // ...{disabled: field?.editReadOnly},
                              sx:
                                field?.type === "text" ||
                                field?.type === "password"
                                  ? { height: "36px" }
                                  : { padding: "10px 4px", height: "36px" },
                            },
                          },
                          error: error && error[field?.id],
                        }}
                      />
                    );
                  })}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </>
  );
};
export default DeliveryForm;
